/**
 * CashFlowChartBuilder.buildPoints: the "This Month" cash line. Past ledger events and
 * waypoint adjustments snake from the period start to today; future income, expense and
 * debt occurrences step on from today's cash.
 */
import { addMonths, daysInMonth, fromYmd, toYmd, type Day } from '../core/calendar.ts';
import { add, dec, isZero, negate, sub, sum, toDouble, gte, type Decimal } from '../core/decimal.ts';
import { frequencyStep, scheduleDate } from '../models/frequency.ts';
import { effectivePaymentDay, paidOn } from '../models/transaction.ts';
import { debtPayment } from './debt-projector.ts';
import type { Checkpoint, Transaction, TransactionType } from '../types/domain.ts';

export interface PastCashEvent {
  readonly date: Day;
  readonly cashImpact: Decimal;
  readonly label: string;
  readonly type: TransactionType;
}

export interface ChartTransaction {
  readonly label: string;
  readonly amount: Decimal;
  readonly type: TransactionType;
  readonly isWaypointAdjustment: boolean;
}

export interface ChartPoint {
  readonly date: Day;
  readonly value: number;
  readonly isProjected: boolean;
  readonly transactions: readonly ChartTransaction[];
}

export interface ChartInputs {
  readonly transactions: readonly Transaction[];
  readonly checkpoints: readonly Checkpoint[];
  readonly startingCash: Decimal;
  readonly pastEvents: readonly PastCashEvent[];
  readonly periodStart: Day;
  readonly periodEnd: Day;
  readonly today: Day;
}

interface DatedEntry {
  readonly date: Day;
  readonly entry: ChartTransaction;
}

interface DayGroup {
  readonly date: Day;
  readonly amount: Decimal;
  readonly transactions: readonly ChartTransaction[];
}

const chartEntry = (label: string, amount: Decimal, type: TransactionType, isWaypointAdjustment = false): ChartTransaction =>
  ({ label, amount, type, isWaypointAdjustment });

/** Group by day in first-seen order, amounts summed in insertion order; returned sorted by day. */
function groupByDay(entries: readonly DatedEntry[]): readonly DayGroup[] {
  const groups = new Map<Day, DayGroup>();
  for (const { date, entry } of entries) {
    const existing = groups.get(date);
    groups.set(date, existing
      ? { date, amount: add(existing.amount, entry.amount), transactions: [...existing.transactions, entry] }
      : { date, amount: entry.amount, transactions: [entry] });
  }
  return [...groups.values()].sort((a, b) => a.date - b.date);
}

const pastLedgerEntries = (inputs: ChartInputs): readonly DatedEntry[] =>
  inputs.pastEvents
    .filter((e) => e.date >= inputs.periodStart && e.date < inputs.today)
    .map((e) => ({ date: e.date, entry: chartEntry(e.label, e.cashImpact, e.type) }));

/** Confirmed-cash deltas between consecutive checkpoints (sorted by date) that fall in the past part of the period. */
function waypointEntries(inputs: ChartInputs): readonly DatedEntry[] {
  const sorted = [...inputs.checkpoints].sort((a, b) => a.date - b.date);
  return sorted.flatMap((checkpoint, index): DatedEntry[] => {
    const date = checkpoint.date;
    if (date < inputs.periodStart || date > inputs.periodEnd || date >= inputs.today || index === 0) return [];
    const delta = sub(checkpoint.confirmedBalances.cash, sorted[index - 1]!.confirmedBalances.cash);
    if (isZero(delta)) return [];
    return [{ date, entry: chartEntry('Adjustment', delta, gte(delta, dec(0)) ? 'income' : 'expense', true) }];
  });
}

/**
 * Candidate dates in the period. Daily/weekly/fortnightly/custom are anchored at the start
 * (R6); monthly/quarterly/yearly all fall on the payment day of the period's month and the
 * next (0 = last day). A custom interval ≤ 0 has no occurrences (R8).
 */
export function chartOccurrences(transaction: Transaction, periodStart: Day, periodEnd: Day): readonly Day[] {
  const frequency = transaction.frequency;
  if (frequency === 'oneTime') return [transaction.startDate];
  if (frequency === 'monthly' || frequency === 'quarterly' || frequency === 'yearly') {
    const paymentDay = effectivePaymentDay(transaction);
    return [0, 1].map((offset) => {
      const { year, month } = toYmd(addMonths(periodStart, offset));
      const length = daysInMonth(year, month);
      return fromYmd(year, month, paymentDay === 0 ? length : Math.min(paymentDay, length));
    });
  }
  const step = frequencyStep(frequency)!;
  if (step.count <= 0) return [];
  const dates: Day[] = [];
  for (let n = 0, cursor = transaction.startDate; cursor <= periodEnd; cursor = scheduleDate(transaction.startDate, step, ++n)) {
    if (cursor >= periodStart) dates.push(cursor);
  }
  return dates;
}

const CASH_IMPACT_SIGN: Partial<Record<TransactionType, (amount: Decimal) => Decimal>> = {
  income: (amount) => amount,
  expense: negate,
  debt: negate,
};

/** Future occurrences on/after today within the period; a repeated (date, label) pair is kept once. */
function futureEntries(inputs: ChartInputs): readonly DatedEntry[] {
  const entries: DatedEntry[] = [];
  for (const transaction of inputs.transactions) {
    const sign = CASH_IMPACT_SIGN[transaction.type];
    if (!transaction.isActive || sign === undefined) continue;
    for (const date of chartOccurrences(transaction, inputs.periodStart, inputs.periodEnd)) {
      if (date < inputs.today || date > inputs.periodEnd || date < transaction.startDate) continue;
      if (entries.some((e) => e.date === date && e.entry.label === transaction.label)) continue;
      const amount = transaction.type === 'debt' ? debtPayment(transaction, date, inputs.today) : paidOn(transaction, date);
      if (transaction.type === 'debt' && isZero(amount)) continue;
      entries.push({ date, entry: chartEntry(transaction.label, sign(amount), transaction.type) });
    }
  }
  return entries;
}

/** Running totals over day groups, one point per group. */
const runningPoints = (start: Decimal, groups: readonly DayGroup[], isProjected: boolean): readonly ChartPoint[] =>
  groups.reduce<{ readonly total: Decimal; readonly points: readonly ChartPoint[] }>(
    ({ total, points }, group) => {
      const next = add(total, group.amount);
      return { total: next, points: [...points, { date: group.date, value: toDouble(next), isProjected, transactions: group.transactions }] };
    },
    { total: start, points: [] },
  ).points;

export function buildChartPoints(inputs: ChartInputs): readonly ChartPoint[] {
  const ledger = pastLedgerEntries(inputs);
  // The period-start balance backs out ledger events only; waypoint adjustments are added after.
  const periodStartCash = sub(inputs.startingCash, sum(groupByDay(ledger).map((g) => g.amount)));
  const past = groupByDay([...ledger, ...waypointEntries(inputs)]);
  const future = groupByDay(futureEntries(inputs));
  const todayGroup = future.find((g) => g.date === inputs.today);
  const todayCash = todayGroup ? add(inputs.startingCash, todayGroup.amount) : inputs.startingCash;

  const anchor: readonly ChartPoint[] = inputs.periodStart < inputs.today
    ? [{ date: inputs.periodStart, value: toDouble(periodStartCash), isProjected: false, transactions: [] }]
    : [];
  const todayPoint: ChartPoint = {
    date: inputs.today,
    value: toDouble(todayCash),
    isProjected: false,
    transactions: todayGroup?.transactions ?? [],
  };
  return [
    ...anchor,
    ...runningPoints(periodStartCash, past, false),
    todayPoint,
    ...runningPoints(todayCash, future.filter((g) => g.date !== inputs.today), true),
  ];
}
